import { orderPath } from "../../shared/sellerPaths";
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { RESERVATION_HOURS, RESERVATION_NOTE_MAX, type SellerReservation } from "../../shared/market";
import { ErrorMessage, Loading } from "../components/StatusMessage";
import { useAuth } from "../auth/AuthProvider";
import { useI18n } from "../i18n/I18nProvider";
import { ApiError, imageUrl } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { marketApi, type MyListing } from "./api";
import { useMarketCart } from "./cart";
import { cardSpecs, Dialog, useArs } from "./components";

const linkOf = (token: string) => `${window.location.origin}/reserva/${token}`;
const when = (iso: string, locale: "es" | "en") =>
  new Date(iso).toLocaleString(locale === "es" ? "es-AR" : "en-US", { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });

/** The link to send, with copy and WhatsApp (no number: the seller picks the chat). */
function ShareLink({ token }: { token: string }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const url = linkOf(token);
  return (
    <span className="row-actions">
      <code className="small">{url.replace(/^https?:\/\//, "")}</code>
      <button type="button" className="btn btn-link btn-sm" onClick={() => void navigator.clipboard?.writeText(url).then(() => setCopied(true))}>
        {copied ? t("mkt.copied") : t("rsv.copy")}
      </button>
      <a className="btn btn-link btn-sm" href={`https://wa.me/?text=${encodeURIComponent(t("rsv.waText", { url }))}`} target="_blank" rel="noopener noreferrer">
        WhatsApp ↗
      </a>
    </span>
  );
}

/** "Reservar para un cliente": how many, at what price (up to the listed one), a note; then the link to send. */
export function ReserveDialog({ listing, onClose, onCreated }: { listing: MyListing; onClose: () => void; onCreated: () => void }) {
  const { t } = useI18n();
  const ars = useArs();
  const [quantity, setQuantity] = useState("1");
  const [price, setPrice] = useState(String(listing.priceArs));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await marketApi.createReservation({ listingId: listing.id, quantity: Number(quantity), priceArs: Number(price), note });
      setToken(r.token);
      onCreated();
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "";
      const max = err instanceof ApiError ? (err.details as { max?: number } | undefined)?.max : undefined;
      setError(
        code === "price" ? t("rsv.errPrice", { price: ars(listing.priceArs) })
        : code === "not_enough" ? t("rsv.errStock", { count: max ?? 0 })
        : code === "reservation_limit" ? t("rsv.errLimit", { max: max ?? 0 })
        : t("error.generic"),
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog title={t("rsv.title")} onClose={onClose}>
      {token ? (
        <div className="stack-sm">
          <p>{t("rsv.done", { hours: RESERVATION_HOURS })}</p>
          <ShareLink token={token} />
          <p className="muted small">{t("rsv.doneHint")}</p>
          <button type="button" className="btn btn-primary btn-sm" onClick={onClose}>
            {t("rsv.close")}
          </button>
        </div>
      ) : (
        <form className="stack-sm" onSubmit={submit}>
          <p className="muted small">{t("rsv.what", { name: listing.name, hours: RESERVATION_HOURS })}</p>
          <label>
            {t("rsv.quantity")}
            <input type="number" inputMode="numeric" min={1} max={listing.quantity} value={quantity} onChange={(e) => setQuantity(e.target.value)} required />
          </label>
          <label>
            {t("rsv.price")}
            <input type="number" inputMode="numeric" min={1} max={listing.priceArs} value={price} onChange={(e) => setPrice(e.target.value)} required />
          </label>
          <span className="field-hint">{t("rsv.priceHint", { price: ars(listing.priceArs) })}</span>
          <label>
            {t("rsv.note")}
            <input type="text" maxLength={RESERVATION_NOTE_MAX} value={note} placeholder={t("rsv.notePlaceholder")} onChange={(e) => setNote(e.target.value)} />
          </label>
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          <div className="btn-row">
            <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>
              {t("rsv.create")}
            </button>
            <button type="button" className="btn btn-link btn-sm" onClick={onClose}>
              {t("rsv.cancelForm")}
            </button>
          </div>
        </form>
      )}
    </Dialog>
  );
}

/** Mi tienda › Publicaciones: the shop's reservations, the active ones first, with the link and a way to call one off. */
export function ReservationsCard({ reservations, onChanged }: { reservations: SellerReservation[]; onChanged: () => void }) {
  const { t, locale } = useI18n();
  const ars = useArs();
  const [busy, setBusy] = useState<number | null>(null);
  if (!reservations.length) return null;
  const cancel = async (id: number) => {
    if (!window.confirm(t("rsv.confirmCancel"))) return;
    setBusy(id);
    try {
      await marketApi.cancelReservation(id);
    } finally {
      setBusy(null);
      onChanged();
    }
  };
  return (
    <section className="plan-card stack-sm" aria-labelledby="rsv-title">
      <h2 id="rsv-title" className="small-title">
        {t("rsv.listTitle")}
      </h2>
      <ul className="pin-list">
        {reservations.map((r) => {
          const active = r.status === "open" || r.status === "claimed";
          return (
            <li key={r.id} className="stack-sm">
              <span className="pin-name">
                <strong>{r.listingName}</strong> · {r.quantity} × {ars(r.priceArs)}
                {r.note ? ` · «${r.note}»` : ""}
                <br />
                <span className="small muted">
                  {r.status === "open"
                    ? t("rsv.st.open", { date: when(r.expiresAt, locale) })
                    : r.status === "claimed"
                      ? t("rsv.st.claimed", { name: r.buyerName ?? "", date: when(r.expiresAt, locale) })
                      : r.status === "used"
                        ? t("rsv.st.used", { name: r.buyerName ?? "", order: r.orderNumber ?? "" })
                        : t(r.status === "expired" ? "rsv.st.expired" : "rsv.st.cancelled")}
                </span>
              </span>
              {active && (
                <span className="row-actions">
                  {r.status === "open" && <ShareLink token={r.token} />}
                  <button type="button" className="btn btn-link btn-sm danger" disabled={busy === r.id} onClick={() => void cancel(r.id)}>
                    {t("rsv.cancel")}
                  </button>
                </span>
              )}
              {r.status === "used" && r.orderNumber && (
                <Link to={orderPath(r.orderNumber, "seller")} className="btn btn-link btn-sm">
                  {t("rsv.viewOrder")}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** /reserva/:token — the card a seller set aside for whoever has the link: take it and pay, or "not available". */
export function ReservationPage() {
  const { token = "" } = useParams();
  const { t, locale } = useI18n();
  const ars = useArs();
  const auth = useAuth();
  const navigate = useNavigate();
  const cart = useMarketCart();
  const page = useAsync((signal) => marketApi.reservationPage(token, signal), [token, auth.user?.id]);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  if (page.error) return <ErrorMessage onRetry={page.reload} />;
  if (!page.data) return <Loading />;
  const r = page.data;
  if (!r.available) {
    return (
      <div className="container section stack">
        <h1>{t("rsv.pageTitle")}</h1>
        <p>{t("rsv.unavailable")}</p>
        <Link to="/mercado" className="btn btn-outline">
          {t("rsv.toMarket")}
        </Link>
      </div>
    );
  }
  const take = async () => {
    if (!auth.user) {
      navigate(`/ingresar?next=${encodeURIComponent(`/reserva/${token}`)}`);
      return;
    }
    setBusy(true);
    setFailed(false);
    try {
      const c = await marketApi.claimReservation(token);
      cart.add(c.handle, { listingId: r.listing.id, quantity: r.quantity, max: r.quantity, name: r.listing.name, priceArs: r.priceArs, photo: r.listing.catalogImage, offerId: c.offerId });
      navigate(`/mercado/comprar/${c.handle}`);
    } catch {
      setFailed(true);
      page.reload();
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="container section stack">
      <h1>{t("rsv.pageTitle")}</h1>
      <p>{t("rsv.pageWhat", { seller: r.listing.seller.displayName, date: when(r.expiresAt, locale) })}</p>
      {r.note && <p className="notice">«{r.note}»</p>}
      <div className="reservation-card row-actions">
        <img src={imageUrl(r.listing.catalogImage)} alt="" width={120} height={168} />
        <span className="stack-sm">
          <strong>{r.listing.name}</strong>
          <span className="muted small">{cardSpecs(r.listing, locale)}</span>
        </span>
      </div>
      <p>
        <strong>{t("rsv.pagePrice", { count: r.quantity, price: ars(r.priceArs) })}</strong>
        {r.priceArs < r.listing.priceArs && <span className="muted"> {t("rsv.pageListed", { price: ars(r.listing.priceArs) })}</span>}
      </p>
      {failed && <p className="form-error">{t("rsv.unavailable")}</p>}
      <div className="btn-row">
        <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void take()}>
          {auth.user ? t("rsv.buy") : t("rsv.signInToBuy")}
        </button>
      </div>
      <p className="muted small">{t("rsv.pageHint")}</p>
    </div>
  );
}
